import {
  annotationText,
  getOrderedAnnotations,
  materializeArrowPoints,
} from './feedbackAnnotations.js';
import { arrowheadGeometry } from './feedbackGeometry.js';
import { buildFeedbackTextLayout } from './feedbackTextLayout.js';
import { textBackgroundFill } from './feedbackTypes.js';

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function transformFor(annotation) {
  const bounds = annotation.bounds || { x: 0, y: 0, width: 1, height: 1 };
  if (!annotation.rotationDeg) return `translate(${bounds.x} ${bounds.y})`;
  return `translate(${bounds.x} ${bounds.y}) rotate(${annotation.rotationDeg} ${bounds.width / 2} ${bounds.height / 2})`;
}

function annotationTextSvg(annotation) {
  const { clipId, lines, metrics } = buildFeedbackTextLayout({
    ...annotation,
    text: annotationText(annotation),
  });
  return `<defs><clipPath id="${clipId}"><rect x="0" y="0" width="${annotation.bounds?.width || 1}" height="${annotation.bounds?.height || 1}" rx="8" /></clipPath></defs><text x="${metrics.insetX}" y="${metrics.baselineY}" fill="${annotation.style?.textColor || '#0f172a'}" font-size="${metrics.fontSize}" font-weight="600" clip-path="url(#${clipId})">${lines
    .map(
      (line, index) =>
        `<tspan x="${metrics.insetX}" dy="${index === 0 ? 0 : metrics.lineHeight}">${escapeXml(line || ' ')}</tspan>`,
    )
    .join('')}</text>`;
}

export function annotationToSvg(annotation, index = 0) {
  const bounds = annotation.bounds || { x: 0, y: 0, width: 1, height: 1 };
  const label = escapeXml(annotation.id || `A-${index + 1}`);
  if (annotation.type === 'arrow') {
    const markerId = `arrowhead-${String(annotation.id || `A-${index + 1}`).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const [start, end] = materializeArrowPoints(annotation);
    // Same head geometry as the live stage (MarkupStage AnnotationShape) so
    // exports match what the tester drew.
    const head = arrowheadGeometry(start, end);
    return `<g data-annotation-id="${label}" transform="${transformFor(annotation)}"><defs><marker id="${markerId}" markerUnits="userSpaceOnUse" markerWidth="${head.headLength}" markerHeight="${head.headWidth}" refX="${head.headLength}" refY="${head.headWidth / 2}" orient="auto"><path d="M0,0 L${head.headLength},${head.headWidth / 2} L0,${head.headWidth} z" fill="${annotation.style?.strokeColor || '#b42318'}" /></marker></defs><line x1="${start.x - bounds.x}" y1="${start.y - bounds.y}" x2="${end.x - bounds.x}" y2="${end.y - bounds.y}" stroke="${annotation.style?.strokeColor}" stroke-width="${head.strokeWidth}" marker-end="url(#${markerId})" /></g>`;
  }
  if (annotation.type === 'pin') {
    const cx = bounds.width / 2;
    const cy = bounds.height / 2;
    const pinLabel = escapeXml(String(annotation.id || '').replace(/^A-0*/, '') || `${index + 1}`);
    return `<g data-annotation-id="${label}" transform="translate(${bounds.x} ${bounds.y})"><circle cx="${cx}" cy="${cy}" r="${Math.max(bounds.width, bounds.height) / 2}" fill="${annotation.style?.strokeColor}" /><text x="${cx}" y="${cy + 7}" fill="white" font-size="20" font-weight="700" text-anchor="middle">${pinLabel}</text></g>`;
  }
  if (annotation.type === 'text') {
    return `<g data-annotation-id="${label}" transform="${transformFor(annotation)}"><rect x="0" y="0" width="${bounds.width}" height="${bounds.height}" rx="8" fill="${textBackgroundFill(annotation.style?.textBackgroundMode)}" stroke="${annotation.style?.boxColor || annotation.style?.strokeColor}" stroke-width="3" />${annotationTextSvg(annotation)}</g>`;
  }
  if (annotation.type === 'highlight') {
    return `<g data-annotation-id="${label}" transform="${transformFor(annotation)}"><rect x="0" y="0" width="${bounds.width}" height="${bounds.height}" rx="4" fill="${annotation.style?.fillColor || annotation.style?.strokeColor}" opacity="0.26" stroke="${annotation.style?.strokeColor}" stroke-width="3" /></g>`;
  }
  if (annotation.type === 'pen') {
    const points = (annotation.localPoints || []).map(
      (point) => `${point.x * bounds.width},${point.y * bounds.height}`,
    );
    return `<g data-annotation-id="${label}" transform="translate(${bounds.x} ${bounds.y})"><polyline points="${points.join(' ')}" fill="none" stroke="${annotation.style?.strokeColor}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" /></g>`;
  }
  return `<g data-annotation-id="${label}" transform="${transformFor(annotation)}"><rect x="0" y="0" width="${bounds.width}" height="${bounds.height}" rx="4" fill="none" stroke="${annotation.style?.strokeColor}" stroke-width="4" /></g>`;
}

export function buildAnnotatedSvg(stage) {
  const image = stage?.metadata?.image || { width: 1, height: 1 };
  const annotations = getOrderedAnnotations(stage?.metadata?.annotations || []);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${image.width}" height="${image.height}" viewBox="0 0 ${image.width} ${image.height}">
  <image href="${escapeXml(stage?.screenshotDataUrl || '')}" width="${image.width}" height="${image.height}" preserveAspectRatio="none" />
  ${annotations.map(annotationToSvg).join('\n  ')}
</svg>`;
}
